// SkyTracker @skytracker/web — typed REST client.
import type { AircraftEnrichment, PhotoResponse, Position, SearchResponse, TrailResponse } from '@skytracker/shared';

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

async function get<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`${BASE}${path}`, { credentials: 'include' });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`${path} ${res.status}`);
    return (await res.json()) as T;
  } catch (err) {
    console.warn('[api]', path, err);
    return null;
  }
}

async function post<T>(path: string, body: unknown): Promise<T | null> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'skytracker' },
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  return (await res.json()) as T;
}

export interface AircraftPayload {
  icao24: string;
  enrichment: AircraftEnrichment | null;
  position: Position | null;
}

export const fetchAircraft = (hex: string): Promise<AircraftPayload | null> =>
  get<AircraftPayload>(`/api/aircraft/${hex}`);

export const fetchTrail = (hex: string, sinceSec = 900): Promise<TrailResponse | null> =>
  get<TrailResponse>(`/api/aircraft/${hex}/trail?sinceSec=${sinceSec}`);

export const fetchPhoto = (hex: string): Promise<PhotoResponse | null> =>
  get<PhotoResponse>(`/api/photos/${hex}`);

export const search = (q: string, limit = 20): Promise<SearchResponse | null> =>
  get<SearchResponse>(`/api/search?q=${encodeURIComponent(q)}&limit=${limit}`);

export const adminLogin = (username: string, password: string): Promise<{ ok: boolean } | null> =>
  post<{ ok: boolean }>('/api/admin/login', { username, password });
